import { Component, Input, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { FeUtil, TYPE } from '../../hezky-routes';
import { getLocationTree, selectLocation } from './state/location-tree.state';
import { auditTime, BehaviorSubject, combineLatest, map, Subject, takeUntil } from 'rxjs';
import { FlatTreeControl } from '@angular/cdk/tree';
import { NzTreeFlatDataSource, NzTreeFlattener } from 'ng-zorro-antd/tree-view';
import { SelectionModel } from '@angular/cdk/collections';
import { selectedLocations } from './state/location-tree.actions';


class FilteredTreeResult {
  constructor(
    public treeData: TYPE.NzTreeNode[],
    public needsToExpanded: TYPE.NzTreeNode[] = []
  ) {}
}

function filterTreeData(data: TYPE.NzTreeNode[], value: string): FilteredTreeResult {
  const needsToExpanded = new Set<TYPE.NzTreeNode>();
  const lowerValue = value.toLowerCase();
  const _filter = (node: TYPE.NzTreeNode, result: TYPE.NzTreeNode[]): TYPE.NzTreeNode[] => {
    if (node.title.toLowerCase().search(lowerValue) !== -1) {
      result.push(node);
      return result;
    }
    if (Array.isArray(node.children)) {
      const nodes = node.children.reduce((a, b) => _filter(b, a), [] as TYPE.NzTreeNode[]);
      if (nodes.length) {
        const parentNode = { ...node, children: nodes };
        needsToExpanded.add(parentNode);
        result.push(parentNode);
      }
    }
    return result;
  };
  const treeData = data.reduce((a, b) => _filter(b, a), [] as TYPE.NzTreeNode[]);
  return new FilteredTreeResult(treeData, [...needsToExpanded]);
}

@Component({
  selector: 'app-location-tree',
  templateUrl: './location-tree.component.html',
  styles: [`
        ::ng-deep .highlight {
        color: #f50;
      }
  `],
})
export class LocationTreeComponent implements OnInit, OnDestroy {
  nodeData: TYPE.NzTreeNode[] = [];
  @Input() type: 'select' | 'multiselect' | 'view' | 'view-multiselect' | 'view-search' | null = null;
  @Output() nodeSelectEvent = new EventEmitter<boolean>();

  private destroy$ = new Subject<void>();
  flatNodeMap = new Map<TYPE.NzTreeNode, TYPE.NzTreeNode>();
  nestedNodeMap = new Map<TYPE.NzTreeNode, TYPE.NzTreeNode>();
  expandedNodes: TYPE.NzTreeNode[] = [];
  selectedNode = new SelectionModel<TYPE.NzTreeNode>(false);
  selectedNodeData: TYPE.NzTreeNode | null = null;
  selectedLocations: number[] = [];

  transformer = (node: TYPE.NzTreeNode, level: number): TYPE.NzTreeNode => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode = existingNode && existingNode.key === node.key ? existingNode : { ...node, level };
    this.flatNodeMap.set(flatNode as TYPE.NzTreeNode, node);
    this.nestedNodeMap.set(node, flatNode as TYPE.NzTreeNode);
    return flatNode as TYPE.NzTreeNode;
  };
  trackBy = (_: number, node: TYPE.NzTreeNode): string => `${node.key}-${node.title}`;
  hasChild = (_: number, node: TYPE.NzTreeNode): boolean => node.expandable;

  treeFlattener = new NzTreeFlattener<TYPE.NzTreeNode, TYPE.NzTreeNode, TYPE.NzTreeNode>(
    this.transformer,
    node => node.level,
    node => node.expandable,
    node => node.children
  );
  treeControl = new FlatTreeControl<TYPE.NzTreeNode, TYPE.NzTreeNode>(
    node => node.level,
    node => node.expandable,
    { trackBy: flatNode => this.flatNodeMap.get(flatNode)! }
  );
  dataSource = new NzTreeFlatDataSource(this.treeControl, this.treeFlattener);
  search = new BehaviorSubject<string>('');
  searchValue = '';
  originData$ = new BehaviorSubject(this.nodeData);
  
  filteredData$ = combineLatest([this.originData$, this.search.pipe(auditTime(800), map(value => (this.searchValue = value)))])
    .pipe(map(([data, value]) => (value ? filterTreeData(data, value) : new FilteredTreeResult(data)))
  );

  constructor(protected feUtil: FeUtil) { }

  ngOnInit(): void {
    this.filteredData$.pipe(takeUntil(this.destroy$)).subscribe((result: any) => {
      this.dataSource.setData(result.treeData as TYPE.NzTreeNode[]);
      this.treeControl.expandAll();
    })
    this.feUtil.store.select(getLocationTree)
      .pipe(takeUntil(this.destroy$))
      .subscribe((location: TYPE.NzTreeNode[]) => {
        if (location && location.length > 0) {
          this.nodeData = location;
          if (!this.searchValue) {
            this.originData$.next(this.nodeData);
            this.dataSource.setData(this.nodeData);
            this.treeControl.expandAll();
          }
        }
      });

    this.feUtil.store.select(selectLocation).pipe(takeUntil(this.destroy$)).subscribe((locations: any) => {
      if (locations && locations.length > 0) {
        this.selectedLocations = locations.map((location: any) => Number(location));
        if (!this.treeControl.dataNodes) return;
        if (this.selectedLocations.length === 1) {
          const selected = this.treeControl.dataNodes.find(node => this.selectedLocations.includes(Number(node.key)));
          if (selected) this.selectedNode.select(selected);
        }
      }
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onNodeSelection(node: TYPE.NzTreeNode): void {
    this.selectedNode.toggle(node);
    let value: number[] = [];
    if (this.selectedNode.isSelected(node)) {
      this.selectedNodeData = node;
      const collectAllLeafKeys = (currentNode: any): number[] => {
        if (!currentNode) return [];
        if (currentNode.isLeaf || currentNode.type === 'location') {
          return [Number(currentNode.key)];
        }
        let keys: number[] = [];
        const children = currentNode.children || [];
        for (const child of children) {
          keys.push(...collectAllLeafKeys(child));
        }
        return keys;
      };
      if (this.selectedNodeData.expandable && this.selectedNodeData.type === 'group') {
        // Find the exact group node from source data, in case complex references exist
        const findNodeByKey = (nodes: any[], key: string): any => {
          for (const n of nodes) {
            if (n.key === key) return n;
            if (n.children && n.children.length) {
              const found = findNodeByKey(n.children, key);
              if (found) return found;
            }
          }
          return null;
        };
        const selectedGroupNode = findNodeByKey(this.nodeData, this.selectedNodeData.key);
        if (selectedGroupNode) {
          value = collectAllLeafKeys(selectedGroupNode);
        }
      } else {
        value = [Number(node.key)];
      }
      this.nodeSelectEvent.emit(true);
      this.feUtil.store.dispatch(selectedLocations({ selectedLocations: value }));
      localStorage.setItem('INIT', 'false');
    } else {
      this.selectedNodeData = null;
    }
  }
}
